/**
 * Program listing — controller
 * Owns filter/sort/infinite-scroll state; returns a stable view-model.
 * Handlers are useCallback-stable to cut ProgramFilters re-renders.
 */
"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useOnScreen from "@/helpers/Onscreen";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue } from "@/context/utils";
import type { ActiveFilters } from "@/components/atoms/ProgramFilters/ProgramFilters";
import {
  CERT_PARTNERS,
  INITIAL_COUNT,
  PAGE_SIZE,
  SECTION_CATEGORY_MAP,
  SORT_OPTIONS,
  type ProgramListingSectionProps,
  type ProgramListingSectionViewModel,
} from "../model/programListing.types";
import {
  buildDomainOptions,
  buildDurationOptions,
  buildFilterSections,
  buildOptionSelected,
  filterCertifications,
  filterPrograms,
  sortPrograms,
  toProgramCardProps,
} from "../service/programListing.service";

export function useProgramListingController(
  props: ProgramListingSectionProps,
): ProgramListingSectionViewModel {
  const {
    programs,
    country = "IN",
    certifications = [],
    hiddenProgramTypes = [],
    lockedProgramType,
  } = props;

  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(
    lockedProgramType ? { programType: [lockedProgramType] } : {},
  );
  const [sortBy, setSortBy] = useState("Most Popular");
  const [sortOpen, setSortOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);
  const [certPartnerFilters, setCertPartnerFilters] = useState<string[]>([]);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const isFirstRender = useRef(true);

  // Keep latest filter/partner values for GA callbacks without rebinding.
  const activeFiltersRef = useRef(activeFilters);
  const certPartnerFiltersRef = useRef(certPartnerFilters);
  activeFiltersRef.current = activeFilters;
  certPartnerFiltersRef.current = certPartnerFilters;

  useEffect(() => {
    const raw = sessionStorage.getItem("programDomainFilter");
    if (raw) {
      sessionStorage.removeItem("programDomainFilter");
      const domains: string[] = JSON.parse(raw);
      setActiveFilters({ domain: domains });
    }
  }, []);

  const domainOptions = useMemo(
    () => buildDomainOptions(certifications),
    [certifications],
  );

  const durationOptions = useMemo(
    () => buildDurationOptions(programs, certifications),
    [programs, certifications],
  );

  const filterSections = useMemo(
    () =>
      buildFilterSections(hiddenProgramTypes, domainOptions, durationOptions),
    [hiddenProgramTypes, domainOptions, durationOptions],
  );

  const handleFiltersChange = useCallback(
    (newFilters: ActiveFilters) => {
      let nextFilters = newFilters;
      if (lockedProgramType) {
        const types = nextFilters.programType ?? [];
        if (!types.includes(lockedProgramType)) {
          nextFilters = {
            ...nextFilters,
            programType: [lockedProgramType, ...types],
          };
        }
      }

      const prevFilters = activeFiltersRef.current;
      const prevCertPartners = certPartnerFiltersRef.current;
      const hadCert = prevFilters.programType?.includes("Certifications") ?? false;
      const hasCert = nextFilters.programType?.includes("Certifications") ?? false;

      let changedKey: string | null = null;
      let changedOption: string | null = null;
      let action: "add" | "remove" = "add";

      for (const key of Object.keys({ ...prevFilters, ...nextFilters })) {
        const prev = prevFilters[key] ?? [];
        const next = nextFilters[key] ?? [];
        const added = next.find((o) => !prev.includes(o));
        const removed = prev.find((o) => !next.includes(o));
        if (added) {
          changedKey = key;
          changedOption = added;
          action = "add";
          break;
        }
        if (removed) {
          changedKey = key;
          changedOption = removed;
          action = "remove";
          break;
        }
      }

      if (changedKey && changedOption) {
        const newCertPartners = hasCert ? prevCertPartners : [];
        setTimeout(() => {
          pushToDataLayer({
            event: "filter_section_interaction",
            cta_text: changedOption,
            action,
            category: SECTION_CATEGORY_MAP[changedKey!] ?? changedKey,
            option_selected: buildOptionSelected(nextFilters, newCertPartners),
            section_name:
              "Explore Our Wide Range of Online Degree Programs and Certifications",
            page_type: getGlobalStateValue("pageType"),
          });
        }, 2000);
      }

      if (!hadCert && hasCert) setCertPartnerFilters([...CERT_PARTNERS]);
      else if (hadCert && !hasCert) setCertPartnerFilters([]);
      setActiveFilters(nextFilters);
    },
    [lockedProgramType],
  );

  const toggleCertPartner = useCallback(
    (partner: string) => {
      const prevPartners = certPartnerFiltersRef.current;
      const prevFilters = activeFiltersRef.current;
      const isAdding = !prevPartners.includes(partner);
      const next = isAdding
        ? [...prevPartners, partner]
        : prevPartners.filter((p) => p !== partner);

      setCertPartnerFilters(next);

      const currentProgramType = prevFilters.programType ?? [];
      const hasCert = currentProgramType.includes("Certifications");
      let updatedFilters = prevFilters;
      if (next.length === 0 && hasCert) {
        updatedFilters = {
          ...prevFilters,
          programType: currentProgramType.filter((t) => t !== "Certifications"),
        };
        setActiveFilters(updatedFilters);
      } else if (next.length > 0 && !hasCert) {
        updatedFilters = {
          ...prevFilters,
          programType: [...currentProgramType, "Certifications"],
        };
        setActiveFilters(updatedFilters);
      }

      setTimeout(() => {
        pushToDataLayer({
          event: "filter_section_interaction",
          cta_text: partner,
          action: isAdding ? "add" : "remove",
          category: "Industry Partners",
          option_selected: buildOptionSelected(updatedFilters, next),
          section_name:
            "Explore Our Wide Range of Online Degree Programs and Certifications",
          page_type: getGlobalStateValue("pageType"),
        });
      }, 2000);
    },
    [],
  );

  const clearFilters = useCallback(() => {
    setActiveFilters({});
  }, []);

  const filteredCertifications = useMemo(
    () =>
      filterCertifications(certifications, activeFilters, certPartnerFilters),
    [certifications, activeFilters, certPartnerFilters],
  );

  const filteredPrograms = useMemo(
    () => filterPrograms(programs, activeFilters),
    [programs, activeFilters],
  );

  const allFilteredItems = useMemo(
    () => [...filteredPrograms, ...filteredCertifications],
    [filteredPrograms, filteredCertifications],
  );

  const sortedItems = useMemo(
    () => sortPrograms(allFilteredItems, sortBy, country),
    [allFilteredItems, sortBy, country],
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setVisibleCount(INITIAL_COUNT);
    if (sectionRef.current) {
      const top =
        sectionRef.current.getBoundingClientRect().top + window.scrollY - 130;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, [sortedItems]);

  const hasMore = visibleCount < sortedItems.length;
  const activeFilterCount = Object.values(activeFilters).flat().length;

  // Prefetch before the sticky filter reaches the section bottom (unstick/restick flicker).
  const isSentinelVisible = useOnScreen(
    sentinelRef,
    { rootMargin: "320px 0px" },
    false,
  );
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    if (!isSentinelVisible || !hasMore || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, sortedItems.length));
    // Allow the next page only after layout settles from this append.
    requestAnimationFrame(() => {
      loadingMoreRef.current = false;
    });
  }, [isSentinelVisible, sortedItems.length, hasMore]);

  const cardProps = useMemo(
    () =>
      sortedItems
        .slice(0, visibleCount)
        .map((p, i) => toProgramCardProps(p, country, i)),
    [sortedItems, visibleCount, country],
  );

  return {
    sectionRef,
    sentinelRef,
    filterSections,
    activeFilters,
    handleFiltersChange,
    certPartnerFilters,
    toggleCertPartner,
    lockedProgramType,
    filteredProgramsCount: filteredPrograms.length,
    filteredCertificationsCount: filteredCertifications.length,
    sortBy,
    sortOpen,
    setSortOpen,
    setSortBy,
    sortOptions: SORT_OPTIONS,
    cardProps,
    hasResults: allFilteredItems.length > 0,
    clearFilters,
    hasMore,
    activeFilterCount,
    mobileFiltersOpen,
    setMobileFiltersOpen,
    certPartners: CERT_PARTNERS,
  };
}
